import {
  Badge,
  Button,
  ButtonVariant,
  Grid,
  SnackbarVariant,
  Status,
  Text,
  TextSize,
  TextWeight,
  makeStyles,
  useSnackbar
} from '@focaldata/cin-ui-components'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isGlobalAudienceEnabled } from '../../../../constants/featureFlags'
import { stringToStatusMap } from '../../../../controls/ProjectListItem/ProjectListItem.utils'
import { SurveyState } from '../../../../data/gql-gen/dashboard/graphql'
import { useSetBaseMarketV2 } from '../../../../hooks/audience/setBaseMarketV2'
import { useMarketsSpecification } from '../../../../hooks/audience/useMarketsSpecification'
import { useProjectId } from '../../../../hooks/useProjectId'
import { useGetProjectByProjectId } from '../../../../modules/Project/Project.hooks'
import { EditableProjectTitle } from '../../../../modules/Project/ProjectNameAndStatus/EditableProjectTitle'
import { useBaseMarketLabel } from '../../../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { formatThousandsWithCommas } from '../../../../utils/HelperFunctions'
import { parseMarketName } from '../../../../utils/marketFlags'
import ChangeBaseMarketDialog from '../ChangeBaseMarketDialog'
import { ProjectFlowBreadcrumb } from '../ProjectFlowBreadcrumb'
import RemoveMarketDialog from '../RemoveMarketDialog'
import {
  getMarkets,
  getMasterSurvey,
  isProjectInProgress
} from '../markets.utils'
import { AddMarketDialog, AddMarketDialogData } from './AddMarketDialog'
import {
  useAddMarket,
  useMarketsCompletes,
  useMasterQuestionsCount,
  useRemoveMarket
} from './ProjectPage.hooks'

const formatCompletes = (collected: number, target: number) =>
  `${formatThousandsWithCommas(collected)}/${formatThousandsWithCommas(target)}`

/**
 * Multi-market project configuration screen — the landing page after a
 * multi-market project is created, at /project/:projectId.
 *
 * Step 1: real project data via `useGetProjectByProjectId` (name + markets,
 * where a market is a Survey under the project, including the auto-created
 * master as the base market — so a new project starts with one market). Add/Remove Market
 * run real mutations (createSurvey + createFieldworkV2 / removeSurveyFromProject)
 * and refetch the project. The remaining summary cards (Languages / Questions /
 * Completes) and per-market language/progress are wired in later steps against
 * their own queries — shown as zeros until then rather than mocked.
 */

const useStyles = makeStyles()({
  page: { padding: '24px 32px' },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    margin: '20px 0'
  },
  card: { border: '1px solid #E5EAF5', borderRadius: 12, padding: 20 },
  cardLabel: {
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#6B7280'
  },
  panel: { border: '1px solid #E5EAF5', borderRadius: 12, padding: 20 },
  tableHead: {
    display: 'grid',
    gridTemplateColumns: '2fr 0.8fr 1fr 1fr 2fr 1fr',
    padding: '12px 0',
    marginTop: 12,
    borderBottom: '1px solid #E5EAF5',
    color: '#6B7280'
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 0.8fr 1fr 1fr 2fr 1fr',
    padding: '14px 0',
    borderBottom: '1px solid #F1F3F7',
    alignItems: 'center'
  },
  // Highlight the row of the market currently nominated as base.
  baseRow: { background: '#F5F8FF' },
  baseCell: { display: 'flex', alignItems: 'center', gap: 6 },
  baseRadio: { cursor: 'pointer', accentColor: 'var(--primary)' },
  // reset so a native (accessible) <button> can be styled as a link (row
  // Open/Remove actions)
  linkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer'
  },
  rowActions: { display: 'flex', alignItems: 'center', gap: 16 },
  actionOpen: { color: 'var(--primary)' },
  actionRemove: { color: '#DE350B' },
  actionDisabled: { color: '#97A0AF', cursor: 'not-allowed' },
  emptyMarkets: { padding: '20px 0', color: '#6B7280' }
})

const SummaryCard = ({ label, value }: { label: string; value: string }) => {
  const { classes } = useStyles()
  return (
    <div className={classes.card}>
      <Text size={TextSize.s} weight={TextWeight.SemiBold}>
        <span className={classes.cardLabel}>{label}</span>
      </Text>
      <div style={{ marginTop: 6 }}>
        <Text size={TextSize.xl} weight={TextWeight.Bold}>
          {value}
        </Text>
      </div>
    </div>
  )
}

export const ProjectPage = () => {
  const { classes, cx } = useStyles()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const projectId = useProjectId()
  const { project, loading, refetch } = useGetProjectByProjectId()
  const [isAddMarketOpen, setIsAddMarketOpen] = useState(false)
  // Wraps the whole bulk-add loop so the dialog shows a single loading state
  // across every market being created.
  const [isBulkAdding, setIsBulkAdding] = useState(false)
  // The market the Remove confirmation is open for (null = closed). Tracks the
  // display name too so the dialog can name the market being removed.
  const [marketToRemove, setMarketToRemove] = useState<{
    surveyId: string
    name: string
  } | null>(null)
  // The market a base-change confirmation is open for (null = closed), plus
  // whether the set-base mutation is in flight (drives the dialog's busy state).
  const [marketToSetBase, setMarketToSetBase] = useState<{
    surveyId: string
    name: string
  } | null>(null)
  const [settingBase, setSettingBase] = useState(false)

  const { addMarket } = useAddMarket(projectId)
  const { removeMarket, loading: removingMarket } = useRemoveMarket(
    projectId,
    refetch
  )
  const baseMarket = useBaseMarketLabel()

  // The base market is derived from MarketSpecification.isBase (there is no
  // top-level base-survey field). questionnaireAggV2 is a project-level
  // aggregate, so any market's surveyId surfaces every market's isBase.
  const masterSurveyId = getMasterSurvey(project)?.surveyId
  // The base-market column/picker is behind the GlobalAudience feature flag;
  // skip the spec query entirely when it's off.
  const globalAudienceEnabled = isGlobalAudienceEnabled()
  const {
    isBaseBySurveyId,
    baseSurveyId,
    refetch: refetchMarketsSpec
  } = useMarketsSpecification(
    globalAudienceEnabled ? masterSurveyId : undefined
  )
  const setBaseMarket = useSetBaseMarketV2()
  // Markets table drops the BASE column (and narrows the grid) when the flag is off.
  const marketsGridColumns = globalAudienceEnabled
    ? '2fr 0.8fr 1fr 1fr 2fr 1fr'
    : '2fr 1fr 1fr 2fr 1fr'

  // A single setBaseMarketV2(surveyId, true) is one atomic aggregate write: the
  // backend sets isBase=true on this market and isBase=false on every other
  // market (demoting the previous base) in one save, then re-propagates the
  // globals. Do NOT also fire setBaseMarket(previous, false) — a second
  // concurrent read-modify-write of the same Project loses the optimistic-lock
  // race (Conflict detected …) and can leave the project with zero or two bases.
  const performSetBaseMarket = async (surveyId: string) => {
    setSettingBase(true)
    try {
      await setBaseMarket(surveyId, true)
      // Reflect the server's post-swap state. On failure the hook's onError
      // toast fires and this refetch shows the unchanged base, so the
      // query-driven radio never lies about the outcome.
      await refetchMarketsSpec().catch(() => undefined)
    } finally {
      setSettingBase(false)
      setMarketToSetBase(null)
    }
  }

  const handleSelectBaseMarket = (surveyId: string, marketName: string) => {
    if (surveyId === baseSurveyId) {
      return
    }
    // Changing an existing base re-bases the project's global custom audience
    // questions (their owner/editor moves to the newly selected market), so
    // confirm via ChangeBaseMarketDialog first. Setting the first base needs no
    // confirmation.
    if (baseSurveyId) {
      setMarketToSetBase({ surveyId, name: marketName })
      return
    }
    void performSetBaseMarket(surveyId)
  }

  const handleConfirmRemove = async () => {
    if (!marketToRemove) {
      return
    }
    try {
      await removeMarket(marketToRemove.surveyId)
      setMarketToRemove(null)
    } catch (error) {
      // Logged in useRemoveMarket; surface it and keep the dialog open to retry.
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to remove market',
        { variant: SnackbarVariant.Error }
      )
    }
  }

  // The project's market surveys in creation order, the base (master) market
  // first — see getMarkets.
  const markets = getMarkets(project)
  const surveyIds = markets.map((survey) => survey.surveyId)
  // 'cache-first': a market added via useAddMarket seeds its fieldwork into the
  // cache, so its language + target render straight from that response without
  // a refetch (adding a market leaves the survey-id set unchanged after the
  // cache update, so a network-only read would never re-run for it).
  const {
    completesBySurveyId,
    languagesBySurveyId,
    totalCollected,
    totalTarget,
    languageCount
  } = useMarketsCompletes(surveyIds, 'cache-first')
  // Questions live on the shared master questionnaire.
  const questionsCount = useMasterQuestionsCount(projectId, masterSurveyId)

  const handleAddMarkets = async (
    rows: AddMarketDialogData[]
  ): Promise<number> => {
    setIsBulkAdding(true)
    // How many markets were created before the loop stopped. Returned to the
    // dialog so it can drop exactly those rows and keep the rest for retry.
    let created = 0
    try {
      // Sequentially: each addMarket primes its fieldwork cache and appends to
      // the cached project, so the markets table picks up each new surveyId as
      // the id-set grows — no manual reload. A parallel run would race those
      // cache writes. The same country may legitimately be added more than once
      // (e.g. a national market plus a regional boost), so rows are not
      // de-duplicated against the existing markets. addMarket rolls back and
      // throws if a market's fieldwork can't be created, so a failure stops the
      // loop here rather than leaving a half-created market.
      for (const row of rows) {
        // eslint-disable-next-line no-await-in-loop
        await addMarket(row)
        created += 1
      }
      setIsAddMarketOpen(false)
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to add market',
        { variant: SnackbarVariant.Error }
      )
    } finally {
      setIsBulkAdding(false)
    }
    return created
  }

  return (
    <div>
      <ProjectFlowBreadcrumb activeStep="Project" />

      <div className={classes.page}>
        <EditableProjectTitle
          name={loading ? 'Loading…' : project?.name ?? 'Project'}
          projectId={project?.projectId}
          size={TextSize.xl}
          weight={TextWeight.Bold}
        />

        {/* Languages are the distinct set across markets, Questions comes from
            the master questionnaire, and Completes is collected (responses
            service progress) over the markets' total target. */}
        <div className={classes.cards}>
          <SummaryCard label="Markets" value={String(markets.length)} />
          <SummaryCard label="Languages" value={String(languageCount)} />
          <SummaryCard label="Questions" value={String(questionsCount)} />
          <SummaryCard
            label="Completes"
            value={formatCompletes(totalCollected, totalTarget)}
          />
        </div>

        <div className={classes.panel}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Text size={TextSize.l} weight={TextWeight.Bold}>
              Markets
            </Text>
            <Button
              variant={ButtonVariant.SecondaryFilled}
              onClick={() => setIsAddMarketOpen(true)}
            >
              Add markets
            </Button>
          </Grid>

          <div
            className={classes.tableHead}
            style={{ gridTemplateColumns: marketsGridColumns }}
          >
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              MARKET
            </Text>
            {globalAudienceEnabled && (
              <Text size={TextSize.s} weight={TextWeight.SemiBold}>
                BASE
              </Text>
            )}
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              LANGUAGE
            </Text>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              STATUS
            </Text>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              PROGRESS
            </Text>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              ACTIONS
            </Text>
          </div>

          {markets.length === 0 ? (
            <div className={classes.emptyMarkets}>
              <Text size={TextSize.ms}>
                No markets yet. Use “Add markets” to add one.
              </Text>
            </div>
          ) : (
            markets.map((survey) => {
              const completes = completesBySurveyId.get(survey.surveyId)
              const languages = languagesBySurveyId.get(survey.surveyId) ?? []
              // The base market's label comes from the resolved base-market info
              // (a legacy master is named after its project); added markets drop
              // the trailing "(XX)" code for consistency.
              const marketName =
                baseMarket && survey.surveyId === baseMarket.surveyId
                  ? baseMarket.label
                  : parseMarketName(survey.name).label
              const isBase = isBaseBySurveyId.get(survey.surveyId) ?? false
              return (
                <div
                  key={survey.surveyId}
                  className={cx(classes.tableRow, isBase && classes.baseRow)}
                  style={{ gridTemplateColumns: marketsGridColumns }}
                >
                  <Text size={TextSize.ms}>{marketName}</Text>
                  {globalAudienceEnabled && (
                    <span className={classes.baseCell}>
                      <input
                        type="radio"
                        name="baseMarket"
                        className={classes.baseRadio}
                        checked={isBase}
                        onChange={() =>
                          handleSelectBaseMarket(survey.surveyId, marketName)
                        }
                        aria-label={`Set ${marketName} as base market`}
                      />
                    </span>
                  )}
                  <Text size={TextSize.ms}>
                    {languages.length > 0 ? languages.join(', ') : '—'}
                  </Text>
                  <span>
                    <Badge
                      label={survey.state}
                      status={
                        stringToStatusMap.get(survey.state) ?? Status.DRAFT
                      }
                    />
                  </span>
                  <Text size={TextSize.ms}>
                    {formatCompletes(
                      completes?.collected ?? 0,
                      completes?.target ?? 0
                    )}
                  </Text>
                  <span className={classes.rowActions}>
                    <button
                      type="button"
                      className={cx(classes.linkButton, classes.actionOpen)}
                      onClick={() =>
                        navigate(
                          // Draft markets open in the audience editor; once a
                          // market is fielding, Open goes to its results.
                          survey.state === SurveyState.Draft
                            ? `/project/${projectId}/audiences/${survey.surveyId}`
                            : `/project/${projectId}/results/${survey.surveyId}`
                        )
                      }
                    >
                      <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                        Open →
                      </Text>
                    </button>
                    {/* Remove is only safe for draft markets; once a market
                        is fielding/complete, removing it could lose data or
                        break live fieldwork. */}
                    {survey.state === SurveyState.Draft && (
                      <button
                        type="button"
                        className={cx(
                          classes.linkButton,
                          globalAudienceEnabled && isBase
                            ? classes.actionDisabled
                            : classes.actionRemove
                        )}
                        // The base market owns the project's global questions;
                        // removing it would orphan the propagated copies, so
                        // block it until another market is made base.
                        disabled={globalAudienceEnabled && isBase}
                        title={
                          globalAudienceEnabled && isBase
                            ? 'The base market can’t be removed. Set another market as base first.'
                            : undefined
                        }
                        onClick={() =>
                          setMarketToRemove({
                            surveyId: survey.surveyId,
                            name: marketName
                          })
                        }
                      >
                        <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                          Remove
                        </Text>
                      </button>
                    )}
                  </span>
                </div>
              )
            })
          )}
        </div>
      </div>

      <AddMarketDialog
        open={isAddMarketOpen}
        loading={isBulkAdding}
        projectInProgress={isProjectInProgress(project)}
        onAdd={handleAddMarkets}
        onClose={() => setIsAddMarketOpen(false)}
      />

      <RemoveMarketDialog
        open={marketToRemove !== null}
        marketName={marketToRemove?.name}
        busy={removingMarket}
        onConfirm={handleConfirmRemove}
        onClose={() => setMarketToRemove(null)}
      />

      <ChangeBaseMarketDialog
        open={marketToSetBase !== null}
        marketName={marketToSetBase?.name}
        busy={settingBase}
        onConfirm={() => {
          if (marketToSetBase) {
            void performSetBaseMarket(marketToSetBase.surveyId)
          }
        }}
        onClose={() => setMarketToSetBase(null)}
      />
    </div>
  )
}
